import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import {
  AlertCircle,
  Check,
  ChevronDown,
  ChevronUp,
  Clock,
  Lock,
  MapPin,
  Navigation,
  Shield,
  Star,
  Timer,
  X,
} from 'lucide-react'
import { loadStripe, Stripe as StripeJs } from '@stripe/stripe-js'

import { Header } from '../components/Layout/Header'
import { useAuth } from '../contexts/AuthContext'
import { useAudit } from '../hooks/useAudit'
import { useLocalization } from '../contexts/LocalizationContext'
import AnalyticsService from '../services/analyticsService'
import PostbackService from '../services/postbackService'
import emailService from '../services/emailService'

import SubscriptionV6 from './SubscriptionV6'
import { supabase } from '../lib/supabase'
import {
  cancelStripeSubscription,
  confirmStripePayment,
  createStripePaymentIntent,
  getStripeStatus,
  StripeNormalizedStatus,
} from '../services/stripeEdgeService'

export {}

type Provider = 'systempay' | 'stripe'

type PaymentResult =
  | {
      type: 'success' | 'error'
      title: string
      message: string
    }
  | null

// Composant Accordéon pour le mobile (copié de SubscriptionV6)
const AccordionItem = (
  { title, children, isOpen, onClick }: {
    title: React.ReactNode
    children: React.ReactNode
    isOpen: boolean
    onClick: () => void
  },
) => {
  return (
    <div className="border-b border-gray-200 last:border-0">
      <button
        className="w-full py-4 px-4 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors"
        onClick={onClick}
      >
        <span className="font-medium text-gray-900 text-left"><span>{title}</span></span>
        {isOpen ? <ChevronUp className="h-5 w-5 text-gray-500" /> : <ChevronDown className="h-5 w-5 text-gray-500" />}
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="p-4 bg-gray-50 text-sm text-gray-600">{children}</div>
      </div>
    </div>
  )
}

const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY

const SubscriptionVS = () => {
  const navigate = useNavigate()
  const { user, subscription, checkSubscription, loading: authLoading } = useAuth()
  const { logSystemError } = useAudit()
  const { t, formatPrice, config } = useLocalization()

  const [searchParams] = useSearchParams()
  const forcedProviderRaw = searchParams.get('payment')
  const forcedProvider = (forcedProviderRaw === 'stripe' || forcedProviderRaw === 'systempay'
    ? forcedProviderRaw
    : null) as Provider | null

  // IMPORTANT: par défaut on veut SystemPay.
  // On ne force Stripe que si l'utilisateur est arrivé avec un paramètre (ex: /auth?payment=stripe)
  // et uniquement pour cet onglet (sessionStorage), pour éviter que Stripe devienne le défaut permanent.
  const sessionPreferredProvider = (sessionStorage.getItem('preferred_payment_provider') as Provider | null) ?? null

  const [provider, setProvider] = useState<Provider>(() => {
    if (!forcedProvider && sessionPreferredProvider === 'stripe') return 'stripe'
    return 'systempay'
  })

  const effectiveProvider = useMemo<Provider>(() => forcedProvider ?? provider, [forcedProvider, provider])

  // Garde-fou : si on arrive sur /subscription sans query mais que l'onglet a mémorisé Stripe,
  // on réécrit l'URL pour rendre le choix déterministe (comme event_id).
  useEffect(() => {
    if (forcedProvider) return
    if (sessionPreferredProvider !== 'stripe') return

    // On force l'URL (replace pour ne pas polluer l'historique)
    navigate('/subscription?payment=stripe', { replace: true })
  }, [forcedProvider, sessionPreferredProvider, navigate])

  // Stripe status
  const [stripeStatus, setStripeStatus] = useState<StripeNormalizedStatus | null>(null)
  const [stripeError, setStripeError] = useState<string | null>(null)

  // UI state (copié du flow V6)
  const [paymentLoading, setPaymentLoading] = useState(false)
  const [paymentResult, setPaymentResult] = useState<PaymentResult>(null)
  const [showPaymentForm, setShowPaymentForm] = useState(false)
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [showTermsTooltip, setShowTermsTooltip] = useState(false)
  const [openAccordion, setOpenAccordion] = useState<string | null>(null)
  const [paymentSuccess, setPaymentSuccess] = useState(false)
  const [isVerifyingSubscription, setIsVerifyingSubscription] = useState(false)

  // Cancellation modal (copié V6)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  const [cancelLoading, setCancelLoading] = useState(false)

  // Stripe embedded form state
  const [stripeInstance, setStripeInstance] = useState<StripeJs | null>(null)
  const [stripeElements, setStripeElements] = useState<any>(null)
  const [stripeClientSecret, setStripeClientSecret] = useState<string | null>(null)
  const [stripePaymentIntentId, setStripePaymentIntentId] = useState<string | null>(null)
  const [cardComplete, setCardComplete] = useState(false)
  const [stripeFormReady, setStripeFormReady] = useState(false)
  const cardElRef = useRef<any>(null)
  const pollingRef = useRef<NodeJS.Timeout | null>(null)
  const isCancellingRef = useRef(false)

  const toggleAccordion = (id: string) => setOpenAccordion(openAccordion === id ? null : id)

  // Provider selection (same logic as existing SubscriptionVS)
  useEffect(() => {
    const loadProvider = async () => {
      if (!user) return
      if (forcedProvider) {
        setProvider(forcedProvider)
        return
      }

      const { data, error } = await supabase
        .from('profiles')
        .select('payment_provider')
        .eq('id', user.id)
        .single()

      if (error) return

      // Si l'utilisateur a déjà un provider en base, ça devient la source de vérité.
      // Sinon, on applique uniquement le choix temporaire venant de sessionStorage.
      if (data?.payment_provider === 'stripe') {
        setProvider('stripe')
      } else if (data?.payment_provider === 'systempay') {
        setProvider('systempay')
      } else {
        if (sessionPreferredProvider === 'stripe') setProvider('stripe')
        else setProvider('systempay')
      }
    }

    loadProvider()
  }, [user, forcedProvider])

  // Stripe status (for debug + active/trial display)
  useEffect(() => {
    const loadStripeStatus = async () => {
      if (!user) return
      if (effectiveProvider !== 'stripe') return

      try {
        const status = await getStripeStatus()
        setStripeStatus(status.status)
      } catch (e) {
        setStripeError(e instanceof Error ? e.message : t('subscription.stripe.errorGeneric'))
      }
    }

    loadStripeStatus()
  }, [user, effectiveProvider])

  // Init Stripe.js
  useEffect(() => {
    const init = async () => {
      if (effectiveProvider !== 'stripe') return
      if (!STRIPE_PUBLISHABLE_KEY) {
        setStripeError(t('subscription.stripe.missingPublishableKey'))
        return
      }

      try {
        const s = await loadStripe(STRIPE_PUBLISHABLE_KEY)
        if (!s) throw new Error(t('subscription.stripe.cannotLoad'))
        setStripeInstance(s)
      } catch (err) {
        setStripeError(err instanceof Error ? err.message : t('subscription.stripe.loadError'))
      }
    }

    init()
  }, [effectiveProvider])

  // Cleanup
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearTimeout(pollingRef.current)
        pollingRef.current = null
      }
      if (cardElRef.current) {
        try {
          cardElRef.current.destroy()
        } catch {
          // ignore
        }
      }
    }
  }, [])

  // Mount CardElement when we have clientSecret + stripe
  useEffect(() => {
    if (!stripeInstance) return
    if (!showPaymentForm) return
    if (!stripeClientSecret) return

    try {
      // Stripe locale: 'fr' | 'en' | 'auto'
      const stripeLocale = config?.language === 'fr' ? 'fr' : 'en'

      const els = stripeInstance.elements({
        clientSecret: stripeClientSecret,
        locale: stripeLocale,
      })
      setStripeElements(els)

      // Mount card
      const card = els.create('payment', {
        layout: 'tabs',
      })
      card.mount('#stripe-payment-element')
      card.on('change', (event: any) => {
        setCardComplete(!!event.complete)
        if (event.error) setStripeError(event.error.message)
        else setStripeError(null)
      })
      cardElRef.current = card
      setStripeFormReady(true)
    } catch (err) {
      setStripeError(err instanceof Error ? err.message : t('subscription.stripe.elementsInitError'))
    }
  }, [stripeInstance, showPaymentForm, stripeClientSecret])

  const startStripeEmbeddedFlow = async () => {
    if (!user) return
    if (!termsAccepted) {
      setShowTermsTooltip(true)
      return
    }

    if (paymentLoading) return

    try {
      setPaymentLoading(true)
      setPaymentResult(null)
      setStripeError(null)

      AnalyticsService.trackEvent('begin_checkout', {
        event_category: 'subscription',
        event_label: 'trial_vs_stripe_begin',
        currency: 'EUR',
        value: 0,
      })

      const pi = await createStripePaymentIntent()
      setStripeClientSecret(pi.clientSecret)
      setStripePaymentIntentId(pi.setupIntentId)
      setShowPaymentForm(true)

      // Scroll
      setTimeout(() => {
        const el = document.getElementById('payment-section')
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 100)
    } catch (err) {
      logSystemError('STRIPE_CREATE_PAYMENT_INTENT', err, { userEmail: user?.email })
      Sentry.captureException(err)
      setPaymentResult({
        type: 'error',
        title: t('errors.generic'),
        message: err instanceof Error ? err.message : t('errors.generic'),
      })
    } finally {
      setPaymentLoading(false)
    }
  }

  const startStripePolling = () => {
    if (pollingRef.current) return

    const poll = async () => {
      try {
        const status = await getStripeStatus()
        if (status.status === 'trial' || status.status === 'active') {
          pollingRef.current = null

          // Update auth context subscription (best effort)
          await checkSubscription(true)

          // UX: ne pas rediriger si une annulation est en cours
          if (!isCancellingRef.current) {
            setTimeout(() => navigate('/dashboard'), 1000)
          }
          return
        }
      } catch {
        // ignore transient
      }

      if (pollingRef.current) {
        pollingRef.current = setTimeout(poll, 1500)
      }
    }

    pollingRef.current = setTimeout(poll, 1500)

    // global timeout
    setTimeout(() => {
      if (pollingRef.current) {
        clearTimeout(pollingRef.current)
        pollingRef.current = null
      }
    }, 300000)
  }

  const confirmStripeEmbeddedPayment = async () => {
    if (!stripeInstance || !stripeElements) return
    if (!stripePaymentIntentId) return

    try {
      setPaymentLoading(true)
      setStripeError(null)

      // SetupIntent flow (save card for future charges)
      const result = await stripeInstance.confirmSetup({
        elements: stripeElements,
        redirect: 'if_required',
      })

      if (result.error) throw result.error

      // Server-side create subscription (trial)
      await confirmStripePayment(stripePaymentIntentId)

      const eventId = user?.user_metadata?.event_id
      if (eventId) PostbackService.sendPaymentPostback(eventId, 0)

      AnalyticsService.trackSubscription('trial_vs_stripe')

      if (user?.email) {
        emailService.sendTrialWelcomeEmailAsync(user.email, user.email.split('@')[0]).catch(() => undefined)
      }

      setPaymentResult({
        type: 'success',
        title: t('subscription.trial.activated'),
        message: t('subscription.trial.message'),
      })

      setPaymentSuccess(true)
      setShowPaymentForm(false)
      setIsVerifyingSubscription(true)

      startStripePolling()
    } catch (err) {
      logSystemError('STRIPE_CONFIRM_PAYMENT', err, { paymentIntentId: stripePaymentIntentId })
      Sentry.captureException(err)
      setStripeError(err instanceof Error ? err.message : t('errors.generic'))
    } finally {
      setPaymentLoading(false)
    }
  }

  const confirmStripeCancellation = async () => {
    if (!user) return

    if (!window.confirm(t('subscription.stripe.confirmImmediateCancel'))) {
      return
    }

    try {
      isCancellingRef.current = true

      // Stop any pending redirect polling ASAP
      if (pollingRef.current) {
        clearTimeout(pollingRef.current)
        pollingRef.current = null
      }

      setCancelLoading(true)
      setStripeError(null)

      await cancelStripeSubscription()

      // Rafraîchir l'état global
      await checkSubscription(true)

      setShowCancelModal(false)
      setPaymentSuccess(false)
      setIsVerifyingSubscription(false)

      // Rafraîchir le status affiché à droite
      try {
        const status = await getStripeStatus()
        setStripeStatus(status.status)
      } catch {
        // ignore
      }

      // Rester sur la page, qui affichera l'offre si plus d'abonnement
    } catch (err) {
      logSystemError('STRIPE_CANCEL_SUBSCRIPTION', err, { userEmail: user?.email })
      Sentry.captureException(err)
      setStripeError(err instanceof Error ? err.message : t('errors.generic'))
    } finally {
      isCancellingRef.current = false
      setCancelLoading(false)
    }
  }

  // If provider is systempay, keep existing SubscriptionV6
  if (effectiveProvider === 'systempay') {
    return <SubscriptionV6 />
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <Header />

      {/* Cancellation Modal - placeholder (Stripe cancel flow à ajouter si besoin) */}
      {showCancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <h3 className="text-2xl font-bold text-gray-900">{t('subscription.cancelModal.title')}</h3>
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="bg-red-50 border border-red-100 rounded-xl p-5 mb-6">
                <p className="font-semibold text-red-800 mb-3">{t('subscription.cancelModal.subtitle')}</p>
                <ul className="space-y-3">
                  <li className="flex items-start text-red-700">
                    <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                    <span>{t('subscription.cancelModal.losses.realTimeMap')}</span>
                  </li>
                  <li className="flex items-start text-red-700">
                    <Navigation className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                    <span>{t('subscription.cancelModal.losses.gpsNavigation')}</span>
                  </li>
                  <li className="flex items-start text-red-700">
                    <Star className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                    <span>{t('subscription.cancelModal.losses.favorites')}</span>
                  </li>
                  <li className="flex items-start text-red-700 font-bold">
                    <Shield className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                    <span>{t('subscription.cancelModal.losses.savedParkings')}</span>
                  </li>
                </ul>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('subscription.cancelModal.reasonLabel')}
                </label>
                <select
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                >
                  <option value="">{t('subscription.cancelModal.reasons.placeholder')}</option>
                  <option value="too_expensive">{t('subscription.cancelModal.reasons.tooExpensive')}</option>
                  <option value="not_using_enough">{t('subscription.cancelModal.reasons.notUsingEnough')}</option>
                  <option value="found_alternative">{t('subscription.cancelModal.reasons.foundAlternative')}</option>
                  <option value="technical_issues">{t('subscription.cancelModal.reasons.technicalIssues')}</option>
                  <option value="other">{t('subscription.cancelModal.reasons.other')}</option>
                </select>
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="w-full py-4 px-6 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold text-lg shadow-lg shadow-green-200 transition-all transform hover:scale-[1.02]"
                >
                  {t('subscription.cancelModal.keepSubscription')}
                </button>

                <button
                  onClick={confirmStripeCancellation}
                  disabled={cancelLoading}
                  className="w-full py-3 px-6 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-xl font-medium text-sm transition-colors"
                >
                  {cancelLoading ? t('subscription.cancelModal.cancelling') : t('subscription.cancelModal.confirmCancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="w-full lg:max-w-7xl lg:mx-auto px-4 pt-0">
        {authLoading && (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
          </div>
        )}

        {paymentSuccess && !isVerifyingSubscription && (
          <div className="bg-white shadow-lg rounded-xl p-8 text-center animate-fade-in">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">{t('subscription.trial.activated')}</h3>
            <p className="text-gray-600">{t('subscriptionV3.redirectingToDashboard')}</p>
          </div>
        )}

        {isVerifyingSubscription && (
          <div className="bg-white shadow-lg rounded-xl p-8 text-center animate-fade-in">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-6"></div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">{t('subscription.verifying.title')}</h3>
            <p className="text-gray-600">{t('subscription.verifying.messageStripe')}</p>
            <p className="text-sm text-gray-400 mt-4">{t('subscription.verifying.hint')}</p>
          </div>
        )}

        {/* Active Trial State */}
        {!authLoading && subscription && subscription.status === 'trial' && !paymentSuccess && (
          <div className="bg-white shadow-lg rounded-xl overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white text-center">
              <Timer className="w-12 h-12 mx-auto mb-3 opacity-90" />
              <h4 className="text-2xl font-bold mb-1">{t('subscription.trial.activeTitle')}</h4>
              <p className="opacity-90">{t('subscriptionV3.remainingDays', { count: subscription.daysUntilNext })}</p>
            </div>

            <div className="p-6">
              <div className="mb-6 text-center">
                <p className="text-gray-700 mb-4">
                  {t('subscription.trial.activeDescription')}
                </p>
                <p className="text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">
                  {t('subscription.trial.noChargeBeforeEnd')}
                </p>
              </div>

              <button
                onClick={() => navigate('/dashboard')}
                className="w-full py-4 px-6 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold text-lg shadow-lg shadow-green-200 mb-6 transition-all transform hover:scale-[1.02] flex items-center justify-center"
              >
                <span>{t('subscription.trial.goToDashboard')}</span>
                <ChevronDown className="ml-2 h-5 w-5 rotate-[-90deg]" />
              </button>

              <div className="border-t border-gray-100 pt-6">
                <button
                  onClick={() => setShowCancelModal(true)}
                  className="w-full py-2 px-4 text-gray-400 hover:text-red-600 text-sm font-medium transition-colors hover:underline"
                >
                  {t('subscriptionV3.cancelTrial')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* No Subscription / Sales Page */}
        {!authLoading && (!subscription || (subscription.status !== 'active' && subscription.status !== 'trial')) && (
          <div className="space-y-6">
            <div className="lg:grid lg:grid-cols-2 lg:gap-8 lg:items-start">
              <div className="space-y-6">
                <div className={`text-center space-y-4 lg:text-left ${showPaymentForm ? 'hidden' : ''}`}>
                  <h1 className="text-3xl font-extrabold text-green-600 leading-tight">{t('subscriptionV6.heroTitle')}</h1>
                  <p className="text-lg text-gray-600">{t('subscriptionV6.heroSubtitle')}</p>

                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mt-6">
                    <ul className="space-y-3 text-left mb-8">
                      <li className="flex items-start">
                        <div className="bg-green-100 rounded-full p-1 mr-3 mt-0.5">
                          <Check className="h-3 w-3 text-green-600" />
                        </div>
                        <span className="text-gray-700">{t('subscriptionV6.features.realTimeMap')}</span>
                      </li>
                      <li className="flex items-start">
                        <div className="bg-green-100 rounded-full p-1 mr-3 mt-0.5">
                          <Check className="h-3 w-3 text-green-600" />
                        </div>
                        <span className="text-gray-700">{t('subscriptionV6.features.gpsNavigation')}</span>
                      </li>
                      <li className="flex items-start">
                        <div className="bg-green-100 rounded-full p-1 mr-3 mt-0.5">
                          <Check className="h-3 w-3 text-green-600" />
                        </div>
                        <span className="text-gray-700">{t('subscriptionV6.features.easyCancellation')}</span>
                      </li>
                    </ul>

                    <div className="flex items-start mb-4 text-left relative">
                      <div className="flex items-center h-5">
                        <input
                          id="terms-checkbox"
                          type="checkbox"
                          checked={termsAccepted}
                          onChange={(e) => {
                            setTermsAccepted(e.target.checked)
                            if (e.target.checked) setShowTermsTooltip(false)
                          }}
                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded cursor-pointer"
                        />
                      </div>
                      <label htmlFor="terms-checkbox" className="ml-2 block text-sm text-gray-600 cursor-pointer">
                        {t('subscriptionV6.termsAcceptance')}{' '}
                        <Link to="/terms" className="text-green-600 hover:underline font-medium" target="_blank">
                          {t('subscriptionV6.termsLink')}
                        </Link>
                      </label>

                      {showTermsTooltip && (
                        <div className="absolute top-8 left-0 right-0 z-10 bg-red-50 border border-red-200 rounded-lg p-3 shadow-lg animate-fade-in">
                          <div className="flex items-start">
                            <div className="bg-red-100 rounded-full p-1 mr-2 mt-0.5 flex-shrink-0">
                              <AlertCircle className="h-3 w-3 text-red-600" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-red-900">{t('subscriptionV6.tooltip.title')}</p>
                              <p className="text-xs text-red-700 mt-1">{t('subscriptionV6.tooltip.message')}</p>
                            </div>
                          </div>
                          <div className="absolute -top-2 left-6 w-0 h-0 border-l-4 border-r-4 border-b-4 border-l-transparent border-r-transparent border-b-red-50"></div>
                        </div>
                      )}
                    </div>

                    <button
                      onClick={startStripeEmbeddedFlow}
                      disabled={paymentLoading}
                      className={`w-full py-4 px-6 rounded-xl text-lg font-bold shadow-lg flex items-center justify-center transition-all ${
                        paymentLoading
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed shadow-none'
                          : 'bg-green-600 text-white shadow-green-200 hover:bg-green-700 transform active:scale-95'
                      }`}
                    >
                      {paymentLoading ? (
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                      ) : (
                        <>
                          <span>{t('subscriptionV6.activateAccount')}</span>
                          <ChevronDown className="ml-2 h-5 w-5" />
                        </>
                      )}
                    </button>
                    <p className="text-xs text-gray-400 mt-3 flex items-center justify-center">
                      <Lock className="h-3 w-3 mr-1" /> {t('subscriptionV6.paymentDisclaimer')}
                    </p>
                  </div>
                </div>

                {/* Payment Form Section */}
                <div
                  id="payment-section"
                  className={`bg-white rounded-xl shadow-lg border border-blue-100 ${!showPaymentForm ? 'hidden' : ''}`}
                >
                  <div className="bg-gray-50 px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                    <div>
                      <h3 className="font-bold text-gray-900">{t('subscriptionV6.securePayment')}</h3>
                      <p className="text-xs text-gray-500">{t('subscriptionV6.sslEncryption')}</p>
                    </div>
                    <div className="flex space-x-2">
                      <img src="https://cdn-icons-png.flaticon.com/512/196/196578.png" alt="Visa" className="h-6" />
                      <img
                        src="https://cdn-icons-png.flaticon.com/512/196/196561.png"
                        alt="Mastercard"
                        className="h-6"
                      />
                    </div>
                  </div>

                  <div className="p-6 pt-1">
                    <div className="mb-2 text-center">
                      <p className="text-gray-700 text-sm font-bold">{t('subscriptionV3.paymentMethodRegistration')}</p>
                    </div>

                    <div className="mb-6 bg-blue-50 rounded-lg p-4 flex items-start">
                      <div className="bg-blue-100 rounded-full p-1 mr-3 mt-0.5 flex-shrink-0">
                        <Clock className="h-4 w-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-blue-900">{t('subscriptionV6.totalAmount')}</p>
                      </div>
                    </div>

                    {!stripeFormReady && (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <p className="text-gray-600">{t('subscriptionV6.loadingPaymentForm')}</p>
                      </div>
                    )}

                    {stripeError && (
                      <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start animate-shake mb-4">
                        <AlertCircle className="h-5 w-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 className="text-sm font-bold text-red-900">{t('errors.generic')}</h4>
                          <p className="text-sm text-red-700 mt-1">{stripeError}</p>
                        </div>
                      </div>
                    )}

                    <div id="stripe-payment-element" className="min-h-[300px]" />

                    <button
                      onClick={confirmStripeEmbeddedPayment}
                      disabled={!cardComplete || paymentLoading}
                      className={`w-full mt-4 py-4 px-6 rounded-xl text-lg font-bold shadow-lg flex items-center justify-center transition-all ${
                        paymentLoading
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed shadow-none'
                          : 'bg-green-600 text-white shadow-green-200 hover:bg-green-700 transform active:scale-95'
                      }`}
                    >
                      {paymentLoading ? t('subscription.stripe.validating') : t('subscription.stripe.validateCard')}
                    </button>

                    <div className="mt-4 flex items-center justify-center text-xs text-gray-400">
                      <Lock className="h-3 w-3 mr-1" /> {t('subscription.stripe.securedBy')}
                    </div>

                    <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div>
                        <p className="text-sm text-green-800 font-medium">{t('subscriptionV3.noChargeInfo')}</p>
                        <p className="text-sm text-green-700 mt-2">{t('subscriptionV3.paymentMethodDescription')}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {paymentResult && paymentResult.type === 'error' && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start animate-shake">
                    <AlertCircle className="h-5 w-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="text-sm font-bold text-red-900">{paymentResult.title}</h4>
                      <p className="text-sm text-red-700 mt-1">{paymentResult.message}</p>
                    </div>
                  </div>
                )}

                {/* Benefits Section (from V3) */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-6">{t('subscriptionV3.whyChooseTitle')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Time & Money Savings */}
                    <div className="space-y-4">
                      <div className="flex items-start">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                          <Clock className="h-6 w-6 text-green-600" />
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900 mb-2">{t('subscriptionV3.benefits.saveTime.title')}</h4>
                          <p className="text-gray-600 text-sm leading-relaxed">{t('subscriptionV3.benefits.saveTime.description')}</p>
                        </div>
                      </div>

                      <div className="flex items-start">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                          <MapPin className="h-6 w-6 text-blue-600" />
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900 mb-2">{t('landing.features.smartMap.title')}</h4>
                          <p className="text-gray-600 text-sm leading-relaxed">{t('subscriptionV3.benefits.smartMap.description')}</p>
                        </div>
                      </div>

                      <div className="flex items-start">
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                          <Navigation className="h-6 w-6 text-purple-600" />
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900 mb-2">{t('landing.features.navigation.title')}</h4>
                          <p className="text-gray-600 text-sm leading-relaxed">{t('subscriptionV3.benefits.navigation.description')}</p>
                        </div>
                      </div>
                    </div>

                    {/* Comfort & Peace of Mind */}
                    <div className="space-y-4">
                      <div className="flex items-start">
                        <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                          <Star className="h-6 w-6 text-orange-600" />
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900 mb-2">{t('landing.features.favorites.title')}</h4>
                          <p className="text-gray-600 text-sm leading-relaxed">{t('subscriptionV3.benefits.favorites.description')}</p>
                        </div>
                      </div>

                      <div className="flex items-start">
                        <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                          <AlertCircle className="h-6 w-6 text-red-600" />
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900 mb-2">{t('subscriptionV3.benefits.notifications.title')}</h4>
                          <p className="text-gray-600 text-sm leading-relaxed">{t('subscriptionV3.benefits.notifications.description')}</p>
                        </div>
                      </div>

                      <div className="flex items-start">
                        <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                          <Shield className="h-6 w-6 text-indigo-600" />
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900 mb-2">{t('subscriptionV3.benefits.support.title')}</h4>
                          <p className="text-gray-600 text-sm leading-relaxed">{t('subscriptionV3.benefits.support.description')}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* FAQ Accordion */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
                  <AccordionItem
                    title={t('subscriptionV6.faq.title')}
                    isOpen={openAccordion === 'faq'}
                    onClick={() => toggleAccordion('faq')}
                  >
                    <div className="space-y-4">
                      <div>
                        <p className="font-medium text-gray-900">{t('subscriptionV6.faq.billingQuestion')}</p>
                        <p>{t('subscriptionV6.faq.billingAnswer', { price: formatPrice() })}</p>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{t('subscriptionV6.faq.cancelQuestion')}</p>
                        <p>{t('subscriptionV6.faq.cancelAnswer')}</p>
                      </div>
                    </div>
                  </AccordionItem>
                </div>
              </div>

              {/* Right Column - Statistics */}
              <div className="space-y-6">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h4 className="text-xl font-bold text-gray-900 mb-4 text-center">{t('subscriptionV6.stats.title')}</h4>
                  <div className="grid grid-cols-2 gap-4 text-center mb-6">
                    <div>
                      <div className="text-2xl font-bold text-green-600 mb-1">15-20 min</div>
                      <div className="text-xs text-gray-600">{t('subscriptionV6.stats.timeSaved')}</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-blue-600 mb-1">98%</div>
                      <div className="text-xs text-gray-600">{t('subscriptionV6.stats.satisfaction')}</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-purple-600 mb-1">200€+</div>
                      <div className="text-xs text-gray-600">{t('subscriptionV6.stats.savings')}</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-orange-600 mb-1">{formatPrice()}</div>
                      <div className="text-xs text-gray-600">{t('subscriptionV6.stats.price')}</div>
                    </div>
                  </div>
                </div>

                <div className="text-center py-6 opacity-60 grayscale">
                  <div className="flex justify-center space-x-4 mb-2">
                    <img src="https://cdn-icons-png.flaticon.com/512/196/196578.png" alt="Visa" className="h-6" />
                    <img src="https://cdn-icons-png.flaticon.com/512/196/196561.png" alt="Mastercard" className="h-6" />
                    <img src="https://cdn-icons-png.flaticon.com/128/16009/16009219.png" alt="CB" className="h-6" />
                  </div>
                  <p className="text-xs text-gray-500">{t('subscription.footer.securePayment')}</p>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="text-sm text-gray-700 space-y-1">
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Active Subscription */}
        {!authLoading && subscription && subscription.status === 'active' && (
          <div className="bg-white shadow-lg rounded-xl p-6">
            <div className="flex items-center mb-4">
              <div className="bg-green-100 rounded-full p-2 mr-3">
                <Check className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">{t('subscription.activeSubscription.title')}</h3>
                <p className="text-sm text-gray-500">{t('subscription.activeSubscription.premium')}</p>
              </div>
            </div>

            <div className="border-t border-gray-100 pt-4 space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">{t('subscription.activeSubscription.nextPayment')}</span>
                <span className="font-medium">
                  {subscription.nextBilling ? new Date(subscription.nextBilling).toLocaleDateString() : t('common.na')}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t('subscription.activeSubscription.price')}</span>
                <span className="font-medium">{formatPrice(subscription.price)}{t('common.perMonth')}</span>
              </div>
            </div>

            <button
              onClick={() => setShowCancelModal(true)}
              className="w-full mt-6 py-2 px-4 border border-gray-300 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-50"
            >
              {t('subscriptionV3.cancelSubscription')}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default SubscriptionVS
